import { Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import CheckboxComponent from 'components/atoms/CheckboxComponent';
import DatePickerComponent from 'components/atoms/DatePickerComponent';
import InputComponent from 'components/atoms/InputComponent';
import InputNumberComponent from 'components/atoms/InputNumberComponent';
import InputTextArea from 'components/atoms/InputTextArea';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import PrilohyTable from 'components/molecules/Dokumenty/PrilohyTable';
import FormWrapper from 'components/molecules/FormWrapper';

import { EduvodZmenyHodnoty, IPrikazZmenHodn, PrikazZmenHodnGet, PrikazZmenHodnPut } from 'services/dokumenty/prikazy';
import { ISmlouvniStrany } from 'services/dokumenty/smlouva';

import { isIsinDlt } from '../NovyDokument';
import { getDataIsins } from './Zruseni';

interface IPrikazJmenovitaComponent {
    nextStep: () => void;
    zadostID: number;
    prikazyData: ISmlouvniStrany;
}

const PrikazJmenovita: React.FC<IPrikazJmenovitaComponent> = ({ nextStep, zadostID, prikazyData }) => {
    const { dokumenty, cisMeny } = useContext(Context);
    const [form] = Form.useForm();
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [, rerender] = useState<boolean>(false);
    const [data, setData] = useState<IPrikazZmenHodn>();
    const [dataIsins] = useState<
        {
            labelcz: string;
            value: string;
        }[]
    >(
        getDataIsins(dokumenty).map(e => {
            return {
                labelcz: e.isin,
                value: e.isin
            };
        }) || []
    );

    const [selectedIsin, setSelectIsin] = useState<string>();
    const [isDLT, setIsDLT] = useState<boolean>();
    useEffect(() => {
        if (selectedIsin) {
            setIsDLT(isIsinDlt(selectedIsin, dokumenty!));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIsin]);

    useEffect(() => {
        setLoading(true);
        PrikazZmenHodnGet(zadostID)
            .then(resp => {
                setSelectIsin(resp.isin);
                setData(resp);
                form.setFieldsValue({
                    duvodzmeny: resp.duvodzmeny
                });
            })
            .catch(() => setData({} as IPrikazZmenHodn))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zadostID]);

    return (
        <LoadingWrapper loading={loading}>
            {data && prikazyData && (
                <>
                    <FormWrapper
                        outForm={form}
                        onFinish={values => {
                            setLoading(true);
                            const dataToSend = {
                                ...(values as any),
                                datum: values.datum.format('YYYY-MM-DD')
                            };
                            delete dataToSend.idmbSpolecnost;
                            delete dataToSend.dicdphSpolecnost;
                            delete dataToSend.nazevSpolecnost;
                            delete dataToSend.leiSpolecnost;
                            delete dataToSend.celkhodnotap;
                            delete dataToSend.celkhodnota;
                            PrikazZmenHodnPut(zadostID, dataToSend)
                                .then(() => nextStep())
                                .finally(() => setLoading(false));
                        }}
                    >
                        <Title text="EMITENT" level={5} />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.idmb}
                            label="IDENTIFIKACNI_CISLO"
                            name="idmbSpolecnost"
                            notRequired={true}
                        />
                        {prikazyData?.spolecnost.dicdph && (
                            <InputComponent
                                disabled={true}
                                initialValue={prikazyData?.spolecnost.dicdph}
                                label="ISIN_IDENTIFIKACE_DIC"
                                name="dicdphSpolecnost"
                                notRequired={true}
                            />
                        )}
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.nazev}
                            label="ISIN_IDENTIFIKACE_FIRMA"
                            name="nazevSpolecnost"
                            notRequired={true}
                        />
                        <InputComponent
                            disabled={true}
                            initialValue={prikazyData?.spolecnost.lei}
                            label="LEI_CELE"
                            name="leiSpolecnost"
                            notRequired={true}
                        />
                        <SelectComponent
                            name="isin"
                            labelParagraph={'PRIKAZ_ZNIZENI_ISIN'}
                            defaultValue={data?.isin}
                            data={dataIsins}
                            isFormItem={true}
                            onChange={val => setSelectIsin(val)}
                        />
                        <SelectComponent
                            name="duvodzmeny"
                            labelParagraph={'PRIKAZ_JMENOVITA_DUVOD'}
                            defaultValue={data?.duvodzmeny}
                            data={[
                                {
                                    labelcz: 'PRIKAZ_JMENOVITA_DUVOD_SNIZENI',
                                    value: EduvodZmenyHodnoty.SNIZENI
                                },
                                {
                                    labelcz: 'PRIKAZ_JMENOVITA_DUVOD_ZVYSENI',
                                    value: EduvodZmenyHodnoty.ZVYSENI
                                },
                                {
                                    labelcz: 'PRIKAZ_JMENOVITA_DUVOD_SCHUZE',
                                    value: EduvodZmenyHodnoty.SCHUZE
                                },
                                {
                                    labelcz: 'PRIKAZ_JMENOVITA_DUVOD_JINE',
                                    value: EduvodZmenyHodnoty.JINE
                                }
                            ]}
                            isFormItem={true}
                        />
                        {form.getFieldValue('duvodzmeny') === EduvodZmenyHodnoty.JINE && (
                            <InputTextArea
                                rows={4}
                                name="rozhodnuti"
                                initialValue={data?.rozhodnuti}
                                label="PRIKAZ_JMENOVITA_DUVOD_JINE_POPIS"
                            />
                        )}
                        <DatePickerComponent
                            name="datum"
                            initialValue={data?.datum ? dayjs(data.datum) : undefined}
                            //  disabledDate={current => current < dayjs().add(3, 'days')}
                            label="PRIKAZ_PREVOD_DATUM"
                        />
                        <Title level={5} text="PRIKAZ_JMENOVITA_UDAJE_PRED" dividerTitle={true} />
                        <InputNumberComponent
                            name="hodnotap"
                            label="PRIKAZ_JMENOVITA_UDAJE_PRED_HODNOTA"
                            initialValue={data?.hodnotap}
                            onChangeCustom={val => {
                                form.setFieldsValue({
                                    celkhodnotap:
                                        form.getFieldValue('pocetcpp') && val
                                            ? Number(form.getFieldValue('pocetcpp')) * Number(val)
                                            : undefined
                                });
                                rerender(pv => !pv);
                            }}
                        />
                        <SelectComponent
                            name="menap"
                            labelParagraph="PRIKAZ_JMENOVITA_UDAJE_PRED_MENA"
                            isFormItem={true}
                            data={cisMeny}
                            onChange={() => rerender(pv => !pv)}
                            defaultValue={data?.menap}
                        />
                        <InputNumberComponent
                            name="pocetcpp"
                            label="PRIKAZ_JMENOVITA_UDAJE_PRED_POCET"
                            initialValue={data?.pocetcpp}
                            precision={0}
                            onChangeCustom={val => {
                                form.setFieldsValue({
                                    celkhodnotap:
                                        form.getFieldValue('hodnotap') && val
                                            ? form.getFieldValue('hodnotap') * Number(val)
                                            : undefined,
                                    celkhodnota:
                                        form.getFieldValue('hodnotan') && val
                                            ? Number(form.getFieldValue('hodnotan')) * Number(val)
                                            : undefined
                                });
                                rerender(pv => !pv);
                            }}
                        />
                        <InputNumberComponent
                            name="celkhodnotap"
                            label="PRIKAZ_JMENOVITA_UDAJE_PRED_CELKOVA_HODNOTA"
                            initialValue={data?.pocetcpp * data?.hodnotap || undefined}
                            disabled={true}
                            notRequired={true}
                        />
                        <Title level={5} text="PRIKAZ_JMENOVITA_UDAJE_PO" dividerTitle={true} />
                        <InputNumberComponent
                            name="hodnotan"
                            label="PRIKAZ_JMENOVITA_UDAJE_PO_HODNOTA"
                            initialValue={data?.hodnotan}
                            onChangeCustom={val => {
                                form.setFieldsValue({
                                    celkhodnota:
                                        form.getFieldValue('pocetcpp') && val
                                            ? form.getFieldValue('pocetcpp') * Number(val)
                                            : undefined
                                });
                                rerender(pv => !pv);
                            }}
                        />
                        <SelectComponent
                            name="menan"
                            labelParagraph="PRIKAZ_JMENOVITA_UDAJE_PO_MENA"
                            isFormItem={true}
                            data={cisMeny}
                            onChange={() => rerender(pv => !pv)}
                            defaultValue={data?.menan}
                        />
                        {/* <InputNumberComponent
                            name="pocetcpn"
                            label="PRIKAZ_JMENOVITA_UDAJE_PO_POCET"
                            initialValue={data?.pocetcpn}
                            precision={0}
                            onChangeCustom={val => {
                                form.setFieldsValue({
                                    celkhodnota:
                                        form.getFieldValue('hodnotan') && val
                                            ? form.getFieldValue('hodnotan') * Number(val)
                                            : undefined
                                });
                                rerender(pv => !pv);
                            }}
                        />*/}
                        <InputNumberComponent
                            name="celkhodnota"
                            label="PRIKAZ_JMENOVITA_UDAJE_PO_CELKOVA_HODNOTA"
                            initialValue={data?.celkhodnota}
                            disabled={true}
                            notRequired={true}
                        />
                        <PrilohyTable zadostID={zadostID} required={true} />
                        <Title level={5} text="PRIKAZ_FORMA_PODMINKY_TITLE" />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_JMENOVITA_PODMINKY_DLT_TEXT1' : 'PRIKAZ_JMENOVITA_PODMINKY_TEXT1'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />
                        <Paragraph
                            text={isDLT ? 'PRIKAZ_JMENOVITA_PODMINKY_DLT_TEXT2' : 'PRIKAZ_JMENOVITA_PODMINKY_TEXT2'}
                            style={{
                                display: 'block',
                                marginTop: 10,
                                marginBottom: 10
                            }}
                        />

                        <Row
                            style={{
                                marginTop: 26
                            }}
                        >
                            <CheckboxComponent
                                name="souhlas"
                                checkboxText={<TranslateText>POTVRZENI_CHECKBOX</TranslateText>}
                                onChange={() => setIsChecked(pv => !pv)}
                                checked={isChecked}
                            />
                        </Row>
                        <Row className="button-row">
                            <Col>
                                <ButtonComponent
                                    buttontext="POKRACOVAT"
                                    id="isinPokracovatBtn"
                                    disabled={!isChecked}
                                    htmlType="submit"
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                </>
            )}
        </LoadingWrapper>
    );
};

export default PrikazJmenovita;
